/**
 * Watermark utility
 *
 * Provides a function to apply a repeated diagonal text watermark pattern
 * across an image. The function now accepts just two parameters: the source
 * image Blob and an `options` object for configuration.
 */

/**
 * Configuration options for `applyWatermark`.
 */
export interface ApplyWatermarkOptions {
  /**
   * Text to render as watermark.
   * @default 'ใช้สำหรับกระบวนการสอบเท่านั้น'
   */
  watermarkText?: string;

  /**
   * Opacity of the watermark text (0.0 - 1.0).
   * @default 0.2
   */
  opacity?: number;

  /**
   * Font size (in px) used for watermark text.
   * @default 48
   */
  fontSize?: number;

  /**
   * Font family used for watermark text.
   * @default 'Arial'
   */
  fontFamily?: string;

  /**
   * Font weight used for watermark text.
   * @default 'bold'
   */
  fontWeight?: string | number;

  /**
   * Extra horizontal spacing (in px) between repeated watermark texts.
   * The actual horizontal step is (measured text width + horizontalSpacing).
   * @default 24
   */
  horizontalSpacing?: number;

  /**
   * Extra vertical spacing (in px) between watermark rows.
   * The actual vertical step is (fontSize + verticalSpacing).
   * @default 64
   */
  verticalSpacing?: number;

  /**
   * Rotation angle in degrees (negative values tilt upwards to the right).
   * @default -35
   */
  angleDeg?: number;

  /**
   * Output MIME type passed to `canvas.toBlob`.
   * Common values: 'image/png', 'image/jpeg', 'image/webp' (browser dependent).
   * @default 'image/jpeg'
   */
  outputType?: string;
}

/**
 * Internal defaults separated so they can be imported if needed.
 */
export const DEFAULT_WATERMARK_OPTIONS: Required<
  Pick<
    ApplyWatermarkOptions,
    | 'watermarkText'
    | 'opacity'
    | 'fontSize'
    | 'fontFamily'
    | 'fontWeight'
    | 'horizontalSpacing'
    | 'verticalSpacing'
    | 'angleDeg'
    | 'outputType'
  >
> = {
  watermarkText: 'ใช้เพื่อยืนยันตัวตน สำหรับการสอบเท่านั้น',
  opacity: 0.2,
  fontSize: 72,
  fontFamily: 'Arial',
  fontWeight: 'bold',
  horizontalSpacing: 36,
  verticalSpacing: 96,
  angleDeg: -35,
  outputType: 'image/jpeg',
};

/**
 * Apply a repeated diagonal text watermark to an image.
 *
 * @param imageBlob The original image blob (any supported raster format)
 * @param options Configuration object (see `ApplyWatermarkOptions`)
 * @returns Promise resolving to a new Blob containing the watermarked image
 *
 * Example:
 * const resultBlob = await applyWatermark(fileBlob, {
 *   watermarkText: 'CONFIDENTIAL',
 *   opacity: 0.15,
 *   fontSize: 64,
 *   outputType: 'image/png',
 * });
 */
export function applyWatermark(
  imageBlob: Blob,
  options: ApplyWatermarkOptions = {},
): Promise<Blob> {
  // Merge defaults
  const {
    watermarkText,
    opacity,
    fontSize,
    fontFamily,
    fontWeight,
    horizontalSpacing,
    verticalSpacing,
    angleDeg,
    outputType,
  } = { ...DEFAULT_WATERMARK_OPTIONS, ...options };

  // Basic validation (fail fast with descriptive errors)
  if (!(imageBlob instanceof Blob)) {
    return Promise.reject(new Error('Parameter "imageBlob" must be a Blob instance.'));
  }
  if (typeof watermarkText !== 'string' || !watermarkText.trim()) {
    return Promise.reject(new Error('Option "watermarkText" must be a non-empty string.'));
  }
  if (typeof opacity !== 'number' || opacity < 0 || opacity > 1) {
    return Promise.reject(new Error('Option "opacity" must be a number between 0.0 and 1.0.'));
  }
  if (typeof fontSize !== 'number' || fontSize <= 0) {
    return Promise.reject(new Error('Option "fontSize" must be a positive number.'));
  }
  if (typeof horizontalSpacing !== 'number' || horizontalSpacing < 0) {
    return Promise.reject(new Error('Option "horizontalSpacing" must be a non-negative number.'));
  }
  if (typeof verticalSpacing !== 'number' || verticalSpacing < 0) {
    return Promise.reject(new Error('Option "verticalSpacing" must be a non-negative number.'));
  }
  if (typeof angleDeg !== 'number') {
    return Promise.reject(new Error('Option "angleDeg" must be a number.'));
  }

  if (typeof outputType !== 'string' || !outputType.trim()) {
    return Promise.reject(new Error('Option "outputType" must be a non-empty string.'));
  }

  return new Promise<Blob>((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(
        new Error(
          'Canvas context creation failed. This may be due to browser compatibility issues, disabled hardware acceleration, or insufficient memory.',
        ),
      );
      return;
    }

    const url = URL.createObjectURL(imageBlob);

    img.onload = () => {
      // Set canvas size to match the image
      canvas.width = img.width;
      canvas.height = img.height;

      // Draw original image
      ctx.drawImage(img, 0, 0);

      // Configure watermark
      ctx.save();
      ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
      ctx.fillStyle = '#000';
      ctx.globalAlpha = opacity;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // Rotate canvas for diagonal watermark
      const angle = (angleDeg * Math.PI) / 180;
      ctx.rotate(angle);

      // Calculate spacing
      const textMetrics = ctx.measureText(watermarkText);
      const hSpacing = textMetrics.width + horizontalSpacing;
      const vSpacing = fontSize + verticalSpacing;

      // Calculate coverage area (needs to be larger due to rotation)
      const rotatedWidth = canvas.width * 1.5;
      const rotatedHeight = canvas.height * 1.5;

      // Draw watermark pattern
      for (let x = -rotatedWidth; x < rotatedWidth; x += hSpacing) {
        for (let y = -rotatedHeight; y < rotatedHeight; y += vSpacing) {
          ctx.fillText(watermarkText, x, y);
        }
      }

      ctx.restore();

      // Convert canvas to blob
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(
            new Error(
              'Failed to convert canvas to blob. This may be due to insufficient memory, browser limitations, or security restrictions.',
            ),
          );
        }
      }, outputType);

      // Cleanup
      URL.revokeObjectURL(url);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(
        new Error('Failed to load image. The image may be corrupted or in an unsupported format.'),
      );
    };

    img.src = url;
  });
}
